import React, { useState, useEffect } from 'react';
import { Stethoscope, Star, Award, Clock, IndianRupee } from 'lucide-react';
import { staffService } from '../../../services/staffService';

const DoctorSelector = ({ bookingData, onDataUpdate }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(bookingData?.doctor || null);

  // RE-ENABLED - Load doctors on mount (SAFE - only runs once on mount)
  useEffect(() => {
    loadDoctors();
  }, []); // Empty dependency array = runs only once on mount

  const loadDoctors = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await staffService?.getStaffByRole('doctor');
      setDoctors(data || []);
    } catch (err) {
      setError('Failed to load doctors. Please try again.');
      console.error('Error loading doctors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    onDataUpdate({ doctor });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={loadDoctors}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-purple-800 mb-2">Select Your Doctor</h2>
        <p className="text-gray-600">Choose a specialist for your consultation</p>
      </div>

      {doctors?.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Stethoscope className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No doctors available at the moment</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {doctors?.map((doctor) => (
            <div
              key={doctor?.id}
              onClick={() => handleSelectDoctor(doctor)}
              className={`p-6 border-2 rounded-lg cursor-pointer transition-all hover:shadow-lg ${selectedDoctor?.id === doctor?.id
                ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
                }`}
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0">
                  {doctor?.avatarUrl ? (
                    <img
                      src={doctor?.avatarUrl}
                      alt={doctor?.fullName}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <Stethoscope className="w-8 h-8 text-purple-600" />
                  )}
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-800 mb-1">
                    {doctor?.fullName ? `Dr. ${doctor?.fullName}` : 'Dr.'}
                  </h3>

                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Award className="w-4 h-4" />
                    <span>{doctor?.experienceYears} years experience</span>
                  </div>

                  {doctor?.specializations && doctor?.specializations?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {doctor?.specializations?.slice(0, 3)?.map((spec, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-semibold">{doctor?.rating?.toFixed(1)}</span>
                    </div>

                    <div className="flex items-center gap-1 text-purple-600 font-semibold">
                      <IndianRupee className="w-4 h-4" />
                      <span>{doctor?.consultationFee}</span>
                    </div>
                  </div>

                  {doctor?.bio?.summary && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                      {doctor?.bio?.summary}
                    </p>
                  )}
                </div>
              </div>

              {selectedDoctor?.id === doctor?.id && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <div className="flex items-center gap-2 text-purple-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Selected Doctor</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedDoctor && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Consultation Fee: </span>
            ₹{selectedDoctor?.consultationFee}
          </p>
        </div>
      )}
    </div>
  );
};

export default DoctorSelector;